import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../lib/fetchWithAuth.js';
import { PublicLayout } from '../components/PublicLayout.js';

const API = import.meta.env.VITE_API_URL;

export default function AccountPage(): JSX.Element {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleExport = async (): Promise<void> => {
    setError('');
    const res = await fetchWithAuth(`${API}/auth/account/export`, {}, navigate);
    if (!res.ok) {
      setError('Could not export your data. Try again.');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `velinko-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (confirm !== 'DELETE') {
      setError('Type DELETE to confirm.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const res = await fetchWithAuth(
        `${API}/auth/account`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: password || undefined, confirm }),
        },
        navigate,
      );
      if (res.ok) {
        localStorage.clear();
        navigate('/login', { replace: true });
        return;
      }
      const body = await res.json().catch(() => null);
      setError(body?.fieldErrors?.[0]?.message ?? body?.detail ?? 'Could not delete the account.');
    } catch {
      setError('Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <div className="account">
        <h1>Account</h1>
        <p className="sub">Download or permanently delete everything Velinko stores for you.</p>

        <section className="panel">
          <h2>Your data</h2>
          <p className="sub">A JSON file with your devices, conversations and messages.</p>
          <button type="button" className="btn" onClick={handleExport}>
            Download my data
          </button>
        </section>

        <section className="panel panel-danger">
          <h2>Delete account</h2>
          <p className="sub">
            Removes your account, paired devices, synced messages, commands and sign-in history from
            the server. The phone app stops syncing. Messages on your phone are not touched. This
            cannot be undone.
          </p>
          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={handleDelete} className="auth-form">
            <div className="field">
              <label htmlFor="password">Password (leave empty if you sign in with Google)</label>
              <input
                id="password"
                className="input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="confirm">Type DELETE to confirm</label>
              <input
                id="confirm"
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              className="btn btn-danger-solid"
              disabled={busy || confirm !== 'DELETE'}
            >
              {busy ? 'Deleting…' : 'Delete account permanently'}
            </button>
          </form>
        </section>

        <div className="auth-links">
          <Link to="/dashboard">← Back to dashboard</Link>
        </div>
      </div>
    </PublicLayout>
  );
}
